import React, { ReactElement, ReactNode, useMemo } from 'react';
import {
  IconButton,
  PopoverMenu,
  PopoverMenuItemProps,
} from '@wix/design-system';
import { More } from '@wix/wix-ui-icons-common';
import { cairoPageCtaClicked, cairoTpaSCtaClicked } from '@wix/bex-core/bi';
import { useWixPatternsContainer } from '@wix/bex-core/react';
import { observer } from 'mobx-react-lite';
import { useExtensionMenuItems } from '../../hooks';
import { ExtensionMenuItem } from '../../state';
import { usePageButtonBiParams } from '../usePageButtonBiParams';

export interface MoreActionsItem
  extends Omit<PopoverMenuItemProps, 'text' | 'prefixIcon' | 'onClick'> {
  text: string;
  prefixIcon?: ReactElement;
  onClick: (params?: Record<string, any>) => unknown;
  biName?: string;
}

export interface MoreActionsProps {
  /**
   * Vertical's actions items that appear in the MoreActions Dropdown.
   */
  items?: MoreActionsItem[][];
  /**
   * An id of the container defined in the installed on a site application that contains TPA's actions.
   */
  containerId?: string;

  dataHook?: string;
}

export type MoreActionsElement = ReactElement<
  MoreActionsProps,
  typeof MoreActions
>;

function _MoreActions(params: MoreActionsProps) {
  const { items = [], containerId, dataHook } = params;

  const { pageType, location } = usePageButtonBiParams();
  const state = useExtensionMenuItems({ containerId });

  const { reportBi } = useWixPatternsContainer();

  const allActions = useMemo(() => {
    const groups = [...items, state.menuItems] as (
      | MoreActionsItem
      | ExtensionMenuItem
    )[][];

    const numCtas = groups.reduce((result, group) => result + group.length, 0);
    const flatActions = groups.flat();

    return groups
      .filter((actions) => actions.length !== 0)
      .reduce<ReactNode[]>((result, group, index, array) => {
        return [
          ...result,
          ...group.map((item) => {
            const { prefixIcon, text, onClick, biName, ...rest } = item;
            const isTPA = 'type' in item && item.type === 'TPA';

            return (
              <PopoverMenu.MenuItem
                prefixIcon={prefixIcon}
                text={text}
                onClick={() => {
                  reportBi(
                    cairoPageCtaClicked({
                      ctaName: biName,
                      ctaType: 'More Actions Option',
                      location,
                      moreActionsIndex: flatActions.indexOf(item),
                      numCtas,
                      section: isTPA ? 'TPA' : 'Vertical',
                      componentType: 'Page',
                      pageType,
                    }),
                  );

                  if (isTPA) {
                    reportBi(
                      cairoTpaSCtaClicked({
                        appId: item.appId,
                        componentType: 'Page',
                        numCtas,
                        ctaIndex: flatActions.indexOf(item),
                        ctaName: biName,
                        location,
                        tpaComponentId: item.componentId,
                      }),
                    );
                  }

                  onClick(); // TODO: We don't have any params we need to pass to the TPA here?
                }}
                {...rest}
              />
            );
          }),
          ...(index !== array.length - 1 ? [<PopoverMenu.Divider />] : []),
        ];
      }, []);
  }, [items, state.menuItems]);

  return allActions.length === 0 ? null : (
    <PopoverMenu
      dataHook={dataHook}
      onShow={() => {
        reportBi(
          cairoPageCtaClicked({
            ctaName: 'More Actions',
            ctaType: 'More Actions Dropdown',
            location,
            componentType: 'Page',
            pageType,
          }),
        );
      }}
      minWidth={240}
      textSize="medium"
      triggerElement={
        <IconButton dataHook="more-actions-popover-button" priority="secondary">
          <More />
        </IconButton>
      }
    >
      {allActions}
    </PopoverMenu>
  );
}

export const MoreActions = observer(_MoreActions);
